import React from 'react';
import { observer } from 'mobx-react';
import AgentMark from './AgentMark';
import styles from './AgentStatus.module.scss';
import {
  MESSAGE_ROTATE_MS,
  MIN_PHASE_MS,
  ThoughtStepLike,
  derivePhase,
  phaseMessage,
} from './agentPhases';
import { useMinimumDwell } from './useMinimumDwell';

interface Props {
  /** The live thought-steps for the answer currently being produced. */
  steps?: ThoughtStepLike[];
}

/**
 * The single line shown while the agent is working: an animated mark and a
 * message that changes as the run moves through its phases.
 *
 * This replaces the growing list of finished steps during the run. The full
 * list is still available afterwards, collapsed, via ThoughtProcess.
 */
const AgentStatus: React.FC<Props> = ({ steps }) => {
  // Two tool calls a second apart would otherwise flash a message nobody can
  // read. Each phase gets a minimum time on screen; anything that arrives and
  // is superseded inside that window is skipped rather than queued.
  const phase = useMinimumDwell(derivePhase(steps), MIN_PHASE_MS);
  const [tick, setTick] = React.useState(0);

  // Restart the rotation whenever a real event moves us to a new phase, so the
  // first (plainly true) message for that phase always shows first.
  React.useEffect(() => {
    setTick(0);
  }, [phase]);

  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), MESSAGE_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [phase]);

  return (
    <div className={styles.status} role="status" aria-live="polite">
      <AgentMark />
      <span className={styles.label}>{phaseMessage(phase, tick)}</span>
    </div>
  );
};

export default observer(AgentStatus);
